<template>
  <div class="sm:px-4 sm:py-6">
    <!-- Sync Progress Modal -->
    <SyncProgressModal
      :is-open="isModalOpen"
      sync-type="hours"
      :progress="progress"
      :has-error="hasError"
      :error-message="errorMessage"
      @close="closeSyncModal"
      @retry="retrySync"
    />

    <!-- Weekly Availability Filters -->
    <EmployeeFilter
      v-model="filters"
      :functions="uniqueFunctions"
      :aanspreekpunten="aanspreekpunten || []"
      show-employee-counts
      :hide-availability-mode="viewMode === 'list'"
      :show-activity-categories="viewMode === 'list'"
    >
      <template #actions>
        <div class="flex items-end gap-2">
          <!-- View switcher: grid vs list/timeline -->
          <div
            class="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden"
            role="group"
            aria-label="Weergave"
          >
            <button
              @click="viewMode = 'grid'"
              :class="
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              "
              class="px-2.5 py-2 flex items-center gap-1.5 text-sm transition-colors"
              title="Beschikbaarheid (grid)"
            >
              <TableCellsIcon class="h-4 w-4" />
              <span class="hidden md:inline">Grid</span>
            </button>
            <button
              @click="viewMode = 'list'"
              :class="
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              "
              class="px-2.5 py-2 flex items-center gap-1.5 text-sm transition-colors border-l border-gray-300 dark:border-gray-600"
              title="Activiteiten (lijst)"
            >
              <ListBulletIcon class="h-4 w-4" />
              <span class="hidden md:inline">Lijst</span>
            </button>
          </div>

          <div class="relative">
            <span
              v-if="lastSyncedLabel"
              :title="lastSyncedFull"
              class="absolute bottom-full right-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
            >
              {{ lastSyncedLabel }}
            </span>
            <button
              @click="startSync"
              :disabled="isSyncing"
              class="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap text-sm"
            >
              <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isSyncing }" />
              <span class="hidden sm:inline">Vernieuw</span>
            </button>
          </div>
        </div>
      </template>
    </EmployeeFilter>

    <!-- Grid view: weekly availability -->
    <template v-if="viewMode === 'grid'">
      <div
        v-if="availError"
        class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 text-red-800 dark:text-red-300"
      >
        Fout bij laden van beschikbaarheid: {{ availError.message }}
      </div>
      <div v-else>
        <WeeklyAvailabilityGrid
          v-if="employeesList.length || weeksHeaders.length || availLoading"
          :employees="filteredEmployees"
          :weeks="weeksHeaders.length ? weeksHeaders : defaultWeeksHeaders"
          :loading="availLoading"
          :sort-direction="sortDirection"
          :availability-mode="filters.availability_mode || 'presourcing'"
          @cell-click="openWeekDetails"
          @sort-change="toggleSort"
        />
        <div v-else class="text-center text-gray-600 dark:text-gray-400 py-6">
          Geen resultaten voor gekozen filters.
        </div>
      </div>
    </template>

    <!-- List view: activity timeline -->
    <template v-else>
      <div
        v-if="activitiesError"
        class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 text-red-800 dark:text-red-300"
      >
        Fout bij laden van activiteiten: {{ activitiesError.message }}
      </div>
      <div v-else>
        <EmployeeActivityTimeline
          v-if="filteredActivities.length || activitiesLoading"
          :employees="filteredActivities"
          :weeks="defaultWeeksHeaders"
          :loading="activitiesLoading"
          :categories="filters.activity_categories"
        />
        <div v-else class="text-center text-gray-600 dark:text-gray-400 py-6">
          Geen resultaten voor gekozen filters.
        </div>
      </div>
    </template>

    <!-- Hours Detail Modal (reusable) -->
    <WeekHoursModal
      :open="showModal && !!selectedEmployee && !!selectedWeek"
      :employee="selectedEmployee"
      :week="selectedWeek"
      :period-start="filters.start_date"
      :period-end="filters.end_date"
      @close="closeModal"
    />
  </div>

  <!-- Delete confirmation modal -->
  <ConfirmActionModal
    :open="showDeleteConfirm"
    :title="deleteConfirmTitle"
    :message="deleteConfirmMessage"
    confirm-label="Verwijderen"
    variant="danger"
    @confirm="executeDeleteAction"
    @cancel="showDeleteConfirm = false"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/vue-query'
import { ArrowPathIcon, TableCellsIcon, ListBulletIcon } from '@heroicons/vue/24/outline'
import EmployeeFilter from '@/components/common/inputs/EmployeeFilter.vue'
import WeeklyAvailabilityGrid from '@/components/employee/WeeklyAvailabilityGrid.vue'
import EmployeeActivityTimeline from '@/components/employee/EmployeeActivityTimeline.vue'
import WeekHoursModal from '@/components/common/modals/WeekHoursModal.vue'
import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'
import SyncProgressModal from '@/components/common/modals/SyncProgressModal.vue'
import { hoursService as hoursApi } from '@/services/hoursService.js'
import { employeeService } from '@/services/employeeService.js'
import { prospectsLeadsService } from '@/services/prospectService.js'
import { useSyncWithProgress } from '@/composables/data/useSyncWithProgress'
import { useLastSynced } from '@/composables/data/useLastSynced'
import { SWP_EMPLOYEE_ID } from '@/constants/swp.js'
import { DEFAULT_ACTIVITY_CATEGORIES } from '@/constants/activityCategories.js'

const queryClient = useQueryClient()

// Shared delete confirmation modal state
const showDeleteConfirm = ref(false)
const deleteConfirmTitle = ref('')
const deleteConfirmMessage = ref('')
const pendingDeleteAction = ref(null)

function openDeleteConfirm(title, message, action) {
  deleteConfirmTitle.value = title
  deleteConfirmMessage.value = message
  pendingDeleteAction.value = action
  showDeleteConfirm.value = true
}

async function executeDeleteAction() {
  showDeleteConfirm.value = false
  if (pendingDeleteAction.value) {
    await pendingDeleteAction.value()
    pendingDeleteAction.value = null
  }
}

// Delete employees state
const isDeletingEmployees = ref(false)

const _confirmDeleteEmployees = () => {
  openDeleteConfirm(
    'Medewerkers verwijderen',
    'Weet je zeker dat je alle medewerkers, teams en notities wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    async () => {
      isDeletingEmployees.value = true
      try {
        const result = await employeeService.deleteAll()
        if (result.success) {
          alert(
            `Verwijderd: ${result.data.employees_deleted} medewerkers, ${result.data.teams_deleted} teams, ${result.data.notes_deleted} notities`
          )
          queryClient.invalidateQueries({ queryKey: ['employees'] })
          queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
          queryClient.invalidateQueries({ queryKey: ['employee-activities'] })
          queryClient.invalidateQueries({ queryKey: ['hours-stats'] })
          refetchStats()
          refetchAvailability()
        }
      } catch (error) {
        console.error('Delete failed:', error)
        alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message))
      } finally {
        isDeletingEmployees.value = false
      }
    }
  )
}

// Delete hours state
const isDeletingHours = ref(false)

const _confirmDeleteHours = () => {
  openDeleteConfirm(
    'Uren verwijderen',
    'Weet je zeker dat je alle uren wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    async () => {
      isDeletingHours.value = true
      try {
        const result = await hoursApi.deleteAll()
        if (result.success) {
          alert(`Verwijderd: ${result.data.hours_deleted} uren`)
          queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
          queryClient.invalidateQueries({ queryKey: ['employee-activities'] })
          queryClient.invalidateQueries({ queryKey: ['hours-stats'] })
          refetchStats()
          refetchAvailability()
        }
      } catch (error) {
        console.error('Delete hours failed:', error)
        alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message))
      } finally {
        isDeletingHours.value = false
      }
    }
  )
}

// Delete projects state
const isDeletingProjects = ref(false)

const _confirmDeleteProjects = () => {
  openDeleteConfirm(
    'Projecten verwijderen',
    'Weet je zeker dat je alle projecten, sourcings en workflow data wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    async () => {
      isDeletingProjects.value = true
      try {
        const result = await prospectsLeadsService.deleteAll()
        if (result.success) {
          const {
            prospects_deleted,
            assignments_deleted,
            preferences_deleted,
            workflow_deleted,
            sourcing_status_deleted
          } = result.data
          alert(
            `Verwijderd: ${prospects_deleted} projecten, ${assignments_deleted} toewijzingen, ${preferences_deleted} voorkeuren, ${workflow_deleted} workflow records, ${sourcing_status_deleted} sourcing statussen`
          )
          queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
          queryClient.invalidateQueries({ queryKey: ['prospect-assignments'] })
          queryClient.invalidateQueries({ queryKey: ['workflow'] })
        }
      } catch (error) {
        console.error('Delete projects failed:', error)
        alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message))
      } finally {
        isDeletingProjects.value = false
      }
    }
  )
}

// Sync with progress modal
const {
  isModalOpen,
  isSyncing,
  progress,
  hasError,
  errorMessage,
  syncResult: _syncResult,
  startSync,
  retrySync,
  closeModal: closeSyncModal
} = useSyncWithProgress('hours', hoursApi.sync, {
  onSuccess: () => {
    refetchStats()
    queryClient.invalidateQueries({ queryKey: ['employees'] })
    queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
    queryClient.invalidateQueries({ queryKey: ['employee-activities'] })
    refetchAvailability()
  }
})

const { lastSyncedLabel, lastSyncedFull } = useLastSynced('hours')

// Fetch hours stats
const {
  data: _hoursStats,
  isLoading: _statsLoading,
  error: _statsError,
  refetch: refetchStats
} = useQuery({
  queryKey: ['hours-stats'],
  queryFn: hoursApi.getStats,
  staleTime: 1000 * 60 * 5 // 5 minutes
})

// Helper functions
const _formatDate = dateString => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('nl-NL')
}

// Weekly availability state
const today = new Date()
const currentMonday = new Date(today)
const day = currentMonday.getDay()
const diffToMonday = day === 0 ? -6 : 1 - day // JS: 0=Sun, 1=Mon
currentMonday.setDate(currentMonday.getDate() + diffToMonday)
const toLocalDateStr = date => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const dayOfMonth = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${dayOfMonth}`
}

const mondayStr = toLocalDateStr(currentMonday)
// Calculate end date as Sunday of the 16th week
const sixteenWeeksMonday = new Date(currentMonday)
sixteenWeeksMonday.setDate(sixteenWeeksMonday.getDate() + 7 * 15) // 15 weeks later (16th week Monday)
const sixteenWeeksSunday = new Date(sixteenWeeksMonday)
sixteenWeeksSunday.setDate(sixteenWeeksSunday.getDate() + 6) // Sunday of that week
const sixteenWeeks = toLocalDateStr(sixteenWeeksSunday)
const filters = ref({
  team_ids: [],
  functions: [],
  search: '',
  aanspreekpunt_ids: [],
  availability_mode: 'presourcing',
  activity_categories: [...DEFAULT_ACTIVITY_CATEGORIES],
  start_date: mondayStr,
  end_date: sixteenWeeks
})

// View switcher: 'grid' (weekly availability) or 'list' (activity timeline)
const viewMode = ref(localStorage.getItem('wzt-view-mode') || 'grid')
watch(viewMode, val => localStorage.setItem('wzt-view-mode', val))

// Fetch aanspreekpunten for filter dropdown
const { data: aanspreekpunten } = useQuery({
  queryKey: ['aanspreekpunten'],
  queryFn: employeeService.getAanspreekpunten,
  staleTime: 1000 * 60 * 10 // 10 minutes
})

// Fetch filter options independently (so they don't change when filtering)
const { data: filterOptions } = useQuery({
  queryKey: ['filter-options'],
  queryFn: employeeService.getFilterOptions,
  staleTime: 1000 * 60 * 10 // 10 minutes
})

const {
  data: availabilityData,
  isLoading: availLoading,
  error: availError,
  refetch: refetchAvailability
} = useQuery({
  queryKey: ['weekly-availability', filters],
  queryFn: async () => {
    const params = {
      start_date: filters.value.start_date,
      end_date: filters.value.end_date
    }
    // Add array parameters only if they have values
    if (filters.value.team_ids?.length > 0) {
      params['team_id[]'] = filters.value.team_ids
    }
    if (filters.value.functions?.length > 0) {
      params['function[]'] = filters.value.functions
    }
    if (filters.value.aanspreekpunt_ids?.length > 0) {
      params['aanspreekpunt_id[]'] = filters.value.aanspreekpunt_ids
    }
    if (filters.value.availability_mode && filters.value.availability_mode !== 'presourcing') {
      params.availability_mode = filters.value.availability_mode
    }
    const data = await hoursApi.getWeeklyAvailability(params)
    return data
  },
  staleTime: 1000 * 60 * 2,
  placeholderData: keepPreviousData
})

// Shared param builder: employee filters + date range (no availability_mode,
// which does not apply to the activity timeline).
const buildActivityParams = f => {
  const params = { start_date: f.start_date, end_date: f.end_date }
  if (f.team_ids?.length > 0) params['team_id[]'] = f.team_ids
  if (f.functions?.length > 0) params['function[]'] = f.functions
  if (f.aanspreekpunt_ids?.length > 0) params['aanspreekpunt_id[]'] = f.aanspreekpunt_ids
  return params
}

// Activity timeline data (list view) — only fetched while in list mode.
const {
  data: activitiesData,
  isLoading: activitiesLoading,
  error: activitiesError
} = useQuery({
  queryKey: ['employee-activities', filters],
  queryFn: async () => hoursApi.getEmployeeActivities(buildActivityParams(filters.value)),
  enabled: computed(() => viewMode.value === 'list'),
  staleTime: 1000 * 60 * 2,
  placeholderData: keepPreviousData
})

const filteredActivities = computed(() => {
  const search = (filters.value.search || '').toLowerCase()
  let result = activitiesData?.value?.data || []
  if (search) {
    result = result.filter(e => e.name.toLowerCase().includes(search))
  }
  // Alphabetical (backend already sorts), SWP always last
  return [...result].sort((a, b) => {
    const aIsSWP = a.simplicate_id === SWP_EMPLOYEE_ID
    const bIsSWP = b.simplicate_id === SWP_EMPLOYEE_ID
    if (aIsSWP && !bIsSWP) return 1
    if (!aIsSWP && bIsSWP) return -1
    return (a.name || '').localeCompare(b.name || '')
  })
})

const employeesList = computed(() => availabilityData?.value?.data || [])
const weeksHeaders = computed(() => {
  const first = employeesList.value[0]
  if (!first) return []
  return first.weeks.map(w => ({
    week_start: w.week_start,
    week_end: w.week_end,
    iso_week: w.iso_week,
    iso_year: w.iso_year,
    // Strip "W" prefix if present and show just the number
    iso_label: String(w.iso_week).replace(/^W/i, '')
  }))
})

// Default week headers for loading state (based on filter dates)
const defaultWeeksHeaders = computed(() => {
  const weeks = []
  const startDate = new Date(filters.value.start_date)
  const endDate = new Date(filters.value.end_date)

  // Start from the Monday of the start week
  const current = new Date(startDate)

  while (current <= endDate) {
    const weekStart = current.toISOString().split('T')[0]
    const weekEnd = new Date(current)
    weekEnd.setDate(weekEnd.getDate() + 6)

    // Calculate ISO week number
    const tempDate = new Date(current)
    tempDate.setHours(0, 0, 0, 0)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
    const week1 = new Date(tempDate.getFullYear(), 0, 4)
    const isoWeek =
      1 + Math.round(((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)

    weeks.push({
      week_start: weekStart,
      week_end: weekEnd.toISOString().split('T')[0],
      iso_week: isoWeek,
      iso_year: tempDate.getFullYear(),
      iso_label: `${isoWeek}`
    })

    // Move to next week
    current.setDate(current.getDate() + 7)
  }

  return weeks
})

const _totalFTE = computed(() => {
  if (!employeesList.value.length) return 0
  return employeesList.value.reduce((sum, emp) => sum + (parseFloat(emp.fte_ratio) || 1), 0)
})

// Use filter options from the API (stable, doesn't change with filtering)
const uniqueFunctions = computed(() => filterOptions.value?.functions || [])

// Sort state: 'desc' = most available first (default), 'asc' = least available first
const sortDirection = ref('desc')

const toggleSort = () => {
  sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc'
}

const filteredEmployees = computed(() => {
  const search = (filters.value.search || '').toLowerCase()
  let result = employeesList.value

  // Filter by search term
  if (search) {
    result = result.filter(e => e.name.toLowerCase().includes(search))
  }

  // Sort by average available days, SWP always last
  return [...result].sort((a, b) => {
    const aIsSWP = a.simplicate_id === SWP_EMPLOYEE_ID
    const bIsSWP = b.simplicate_id === SWP_EMPLOYEE_ID
    if (aIsSWP && !bIsSWP) return 1
    if (!aIsSWP && bIsSWP) return -1

    const avgA =
      a.weeks.length > 0
        ? a.weeks.reduce((sum, w) => sum + (parseFloat(w.available_days) || 0), 0) / a.weeks.length
        : 0
    const avgB =
      b.weeks.length > 0
        ? b.weeks.reduce((sum, w) => sum + (parseFloat(w.available_days) || 0), 0) / b.weeks.length
        : 0
    // Apply sort direction
    return sortDirection.value === 'desc' ? avgB - avgA : avgA - avgB
  })
})

// Modal state and handlers
const showModal = ref(false)
const selectedEmployee = ref(null)
const selectedWeek = ref(null)

const openWeekDetails = ({ employee, week }) => {
  selectedEmployee.value = employee
  selectedWeek.value = week
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  selectedEmployee.value = null
  selectedWeek.value = null
}
</script>
